import { useCallback, useMemo, Fragment } from "react";
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Trash2 } from "lucide-react";
import {
  Select,
  TextInput,
  TextInputWithPrefix,
  Button,
} from "../ui";
import {
  calculatePlanPrice,
  applyDiscount,
  calculateCreditsPerBillingCycle,
  formatCurrency,
} from "../../_utils/pricing";
import TierTable, { withDerivedTierBoundaries } from "./TierTable";
import "./_plan-product-picker.scss";

const TIERED_MODELS = ["Volume", "Tiered"];
const DISCOUNT_TYPE_OPTIONS = [
  { label: "Flat", value: "Flat" },
  { label: "Percent", value: "Percent" },
];

let idCounter = 0;
const generateId = () => `catalog-item-${Date.now()}-${idCounter++}`;

const bundleIdOf = (bundle) =>
  typeof bundle === "object" && bundle !== null ? bundle._id : bundle;

const BLANK_ITEM = {
  entityId: null,
  bundleId: "",
  bundleName: "",
  label: "",
  pricingModel: null,
  amount: "",
  pricingTiers: [],
  pricingCycle: "",
  quantity: 1,
  freeCredits: 0,
  discountType: "Flat",
  discount: "",
  basePrice: 0,
  totalPrice: 0,
};

const columnHelper = createColumnHelper();

/**
 * Catalog plan/product selection + quantity, priced via calculatePlanPrice
 * (_utils/pricing.js - ported from the live BillingItems/PlanProductTable
 * formula, not src/utils/calculatePrice.js, which assumes a `priceDetails`
 * shape that doesn't exist on a real catalog entity). A real `<table>`
 * (not a flex row of boxed inputs), rendered via TanStack Table (column
 * defs + flexRender) rather than a hand-mapped `.map()` - purely a
 * rendering-mechanism change, same markup/classNames/styling as before.
 * Column set matches BillingItems/PlanProductTable's own table: Bundle
 * (Plans only), name, Unit price, Qty, Free Credits, Credits per Billing
 * Cycle, Total Wallet Credits, {entity} price, Pricing cycle, Discount
 * type/amount, Total. A row's Volume/Tiered TierTable is a second `<tr>`
 * rendered right after its main row (outside TanStack's own row model,
 * same as before the migration - TanStack doesn't have an opinion on how
 * many `<tr>`s a row occupies).
 *
 * Matches the old table's ADD FLOW too, not just its columns: nothing (no
 * table at all) shows until "+ Add {label}" is clicked; that appends one
 * blank row directly into the table, which the user configures in place
 * (Bundle, then entity, stay live-editable for the row's whole life - not
 * a separate "pick, then Add" staging step with its own commit button).
 *
 * An item is priced from its OWN stored fields (`amount`, `pricingTiers`,
 * `discountType`/`discount`, etc.), not by re-reading the catalog entity
 * it started from - Unit price, tier brackets, and discount are all
 * editable after picking an entity (unlike the name), so re-deriving from
 * the catalog on every quantity change would silently discard those edits.
 * `basePrice` (pre-discount) and `totalPrice` (post-discount, what
 * calculateEntityTotals actually sums) are both stored since the table
 * shows both as separate columns, matching the reference.
 *
 * `entities` is the plan or product list (each needs `_id`, a display
 * name, `pricingModel`, its base price at `amount` (Plan) or `price`
 * (Product), a top-level `pricingTiers` bracket array for Volume/Tiered
 * models, its own `pricingCycle`, and `walletCredits`). `bundlesCatalog`
 * is Plans-only (Products have no bundle concept in the catalog schema) -
 * when provided (even as `[]`), a Bundle picker gates and filters the
 * entity dropdown per-row, matching the old table's behavior; omit the
 * prop entirely for Products. `items` is the raw selected list (see
 * usePricingState); `setRawItems` is the raw setter, called with the
 * functional-updater form throughout so handlers stay stable regardless of
 * current item count.
 */
const PlanProductPicker = ({
  entities = [],
  bundlesCatalog,
  items,
  setRawItems,
  currencySign,
  selectedBillingCycle,
  nameField = "name",
  title = "Plans",
  entityLabel = "Item",
}) => {
  const showBundle = Array.isArray(bundlesCatalog);

  // Defensive: some list queries in this app wrap their result
  // (`{plans: [...]}`/`{products: [...]}`) rather than returning a plain
  // array - a caller passing the un-unwrapped response through would
  // otherwise crash here instead of just rendering an empty picker.
  const safeEntities = useMemo(
    () => (Array.isArray(entities) ? entities : []),
    [entities],
  );
  const safeBundles = useMemo(
    () => (Array.isArray(bundlesCatalog) ? bundlesCatalog : []),
    [bundlesCatalog],
  );
  const bundleOptions = useMemo(
    () =>
      safeBundles.map((bundle) => ({
        label: bundle.bundleName ?? bundle.name,
        value: bundle._id,
      })),
    [safeBundles],
  );

  // The single source of truth for an item's pricing - always computed
  // from the item's OWN fields (see docstring above), shared by every
  // handler below so none of them can drift from this formula. Returns
  // both the pre-discount base price (its own column) and the discounted
  // total (calculateEntityTotals' actual input).
  const priceItem = useCallback(
    (fields) => {
      const basePrice = calculatePlanPrice({
        pricingModel: fields.pricingModel,
        amount: fields.amount,
        pricingTiers: fields.pricingTiers,
        seats: fields.quantity,
        currentPlanCycle: fields.pricingCycle,
        selectedBillingCycle,
      });
      const totalPrice = applyDiscount(basePrice, {
        type: fields.discountType,
        value: fields.discount,
      });
      return { basePrice, totalPrice };
    },
    [selectedBillingCycle],
  );

  // Every field-level edit below follows the same shape: patch the item,
  // re-derive {basePrice, totalPrice} from the patched fields, store all
  // three - so no handler can leave totalPrice out of sync with what it's
  // actually computed from.
  const updateItem = useCallback(
    (id, patch) => {
      setRawItems((prev) =>
        prev.map((item) => {
          if (item.id !== id) return item;
          const updated = { ...item, ...patch };
          const { basePrice, totalPrice } = priceItem(updated);
          return { ...updated, basePrice, value: totalPrice, totalPrice };
        }),
      );
    },
    [priceItem, setRawItems],
  );

  const handleAddRow = useCallback(() => {
    setRawItems((prev) => [...prev, { id: generateId(), ...BLANK_ITEM }]);
  }, [setRawItems]);

  const handleBundleChange = useCallback(
    (id, bundleId) => {
      const bundle = safeBundles.find((b) => b._id === bundleId);
      // The previously-chosen entity may not belong to the new bundle -
      // reset it rather than silently keeping a plan that's no longer a
      // valid option, matching the old table's reset-on-bundle-change.
      updateItem(id, {
        bundleId,
        bundleName: bundle ? (bundle.bundleName ?? bundle.name) : "",
        entityId: null,
        label: "",
        pricingModel: null,
        amount: "",
        pricingTiers: [],
        pricingCycle: "",
        freeCredits: 0,
      });
    },
    [safeBundles, updateItem],
  );

  const handleEntityChange = useCallback(
    (id, entityId) => {
      const entity = safeEntities.find((e) => e._id === entityId);
      if (!entity) return;
      updateItem(id, {
        entityId: entity._id,
        label: entity[nameField],
        pricingModel: entity.pricingModel,
        // A Plan's base price lives at `amount`; a Product's at `price` -
        // this entity never has both, so the fallback picks whichever the
        // real catalog document actually carries.
        amount: entity.amount ?? entity.price,
        pricingTiers: (entity.pricingTiers || []).map((tier) => ({ ...tier })),
        pricingCycle: entity.pricingCycle,
        freeCredits: entity.walletCredits || 0,
        // Was never captured onto the engine item at all, so a usage-based
        // plan's link to its Usage Compilation was silently dropped the
        // moment it was picked here - every subscription created through
        // this flow lost the one field the usage-metering/wallet-debit
        // pipeline (crons/getAllSubscriptionData.js,
        // processUsageAndUpdateWallet.js) actually reads off
        // plans[].usageCompilations. Undefined on a Product entity, same as
        // pricingTiers/pricingCycle above - harmless there.
        usageCompilations: entity.usageCompilations || null,
      });
    },
    [safeEntities, nameField, updateItem],
  );

  const handleAmountChange = useCallback(
    (id, amount) => updateItem(id, { amount }),
    [updateItem],
  );
  const handleQuantityChange = useCallback(
    (id, quantity) => updateItem(id, { quantity }),
    [updateItem],
  );
  const handleDiscountChange = useCallback(
    (id, patch) => updateItem(id, patch),
    [updateItem],
  );

  const handleFreeCreditsChange = useCallback(
    (id, freeCredits) => {
      // Doesn't touch pricing at all - skip priceItem entirely rather than
      // route a non-price field through the shared updateItem helper.
      setRawItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, freeCredits } : item)),
      );
    },
    [setRawItems],
  );

  // `field` is "from" or "unitPrice" - "from" also re-derives every tier's
  // "to" boundary (see withDerivedTierBoundaries), "unitPrice" doesn't
  // touch boundaries at all.
  const handleTierFieldChange = useCallback(
    (id, tierIndex, field, value) => {
      setRawItems((prev) =>
        prev.map((item) => {
          if (item.id !== id) return item;
          let pricingTiers = item.pricingTiers.map((tier, i) =>
            i === tierIndex ? { ...tier, [field]: value } : tier,
          );
          if (field === "from") {
            pricingTiers = withDerivedTierBoundaries(pricingTiers);
          }
          const { basePrice, totalPrice } = priceItem({
            ...item,
            pricingTiers,
          });
          return {
            ...item,
            pricingTiers,
            basePrice,
            value: totalPrice,
            totalPrice,
          };
        }),
      );
    },
    [priceItem, setRawItems],
  );

  const handleTierAdd = useCallback(
    (id) => {
      setRawItems((prev) =>
        prev.map((item) => {
          if (item.id !== id) return item;
          const lastTier = item.pricingTiers[item.pricingTiers.length - 1];
          const nextFrom = lastTier ? (parseFloat(lastTier.from) || 0) + 1 : 1;
          const pricingTiers = withDerivedTierBoundaries([
            ...item.pricingTiers,
            { from: nextFrom, unitPrice: 0 },
          ]);
          const { basePrice, totalPrice } = priceItem({
            ...item,
            pricingTiers,
          });
          return {
            ...item,
            pricingTiers,
            basePrice,
            value: totalPrice,
            totalPrice,
          };
        }),
      );
    },
    [priceItem, setRawItems],
  );

  const handleTierRemove = useCallback(
    (id, tierIndex) => {
      setRawItems((prev) =>
        prev.map((item) => {
          if (item.id !== id) return item;
          const pricingTiers = withDerivedTierBoundaries(
            item.pricingTiers.filter((_, i) => i !== tierIndex),
          );
          const { basePrice, totalPrice } = priceItem({
            ...item,
            pricingTiers,
          });
          return {
            ...item,
            pricingTiers,
            basePrice,
            value: totalPrice,
            totalPrice,
          };
        }),
      );
    },
    [priceItem, setRawItems],
  );

  const handleRemove = useCallback(
    (id) => {
      setRawItems((prev) => prev.filter((item) => item.id !== id));
    },
    [setRawItems],
  );

  const columns = useMemo(() => {
    const cols = [];

    if (showBundle) {
      cols.push(
        columnHelper.accessor("bundleId", {
          id: "bundle",
          header: "Bundle",
          cell: (info) => (
            <Select
              compact
              options={bundleOptions}
              value={info.getValue()}
              onChange={(bundleId) =>
                handleBundleChange(info.row.original.id, bundleId)
              }
              placeholder="Select bundle..."
            />
          ),
        }),
      );
    }

    cols.push(
      columnHelper.accessor("entityId", {
        id: "entity",
        header: entityLabel,
        cell: (info) => {
          const item = info.row.original;
          const entityDisabled = showBundle && !item.bundleId;
          const filtered = showBundle
            ? safeEntities.filter(
                (entity) => bundleIdOf(entity.bundle) === item.bundleId,
              )
            : safeEntities;
          const entityOptions = filtered.map((entity) => {
            const meta = [entity.pricingCycle, entity.pricingModel]
              .filter(Boolean)
              .join(" · ");
            return {
              label: meta
                ? `${entity[nameField]} (${meta})`
                : entity[nameField],
              value: entity._id,
            };
          });
          // Products are only ever fetched Active (server-side filter) - an
          // empty list here means every Draft Product needs activating
          // first. Plans aren't status-filtered at this query, so an empty
          // list here almost always means no Plan's own Bundle field
          // (Plan details tab) matches the bundle picked in this row - a
          // blank/unset Bundle on the Plan is the most common cause.
          const emptyMessage = showBundle
            ? `No ${entityLabel}s found for this Bundle — check the ${entityLabel}'s own Bundle field (Plan details tab) matches the Bundle selected above.`
            : `No active ${entityLabel}s found — ${entityLabel}s must be Activated before they can be added here.`;
          return (
            <Select
              compact
              options={entityOptions}
              value={info.getValue() ?? ""}
              onChange={(entityId) => handleEntityChange(item.id, entityId)}
              disabled={entityDisabled}
              placeholder={entityDisabled ? "Select bundle first" : "Select..."}
              emptyMessage={emptyMessage}
            />
          );
        },
      }),
      columnHelper.display({
        id: "unitPrice",
        header: "Unit price",
        cell: (info) => {
          const item = info.row.original;
          return TIERED_MODELS.includes(item.pricingModel) ? (
            <span className="plan-product-picker__dash">-</span>
          ) : (
            <TextInputWithPrefix
              compact
              prefix={currencySign}
              placeholder="Unit price"
              value={item.amount ?? ""}
              onChange={(value) => handleAmountChange(item.id, value)}
              disabled={!item.pricingModel}
            />
          );
        },
      }),
      columnHelper.accessor("quantity", {
        id: "quantity",
        header: "Qty",
        meta: { className: "plan-product-picker__col-qty" },
        cell: (info) => {
          const item = info.row.original;
          const isQuantityDisabled = item.pricingModel === "Flat Fee";
          return (
            <TextInput
              compact
              value={info.getValue() ?? ""}
              onChange={(value) => handleQuantityChange(item.id, value)}
              disabled={isQuantityDisabled}
              tooltip={
                isQuantityDisabled
                  ? "Flat Fee pricing doesn't change with quantity."
                  : undefined
              }
            />
          );
        },
      }),
      columnHelper.accessor("freeCredits", {
        id: "freeCredits",
        header: "Free Credits",
        cell: (info) => (
          <TextInput
            compact
            value={info.getValue() ?? ""}
            onChange={(value) =>
              handleFreeCreditsChange(info.row.original.id, value)
            }
          />
        ),
      }),
      columnHelper.display({
        id: "creditsPerCycle",
        header: "Credits per Billing Cycle",
        meta: { className: "plan-product-picker__col-computed" },
        cell: (info) => {
          const item = info.row.original;
          return calculateCreditsPerBillingCycle(
            item.quantity,
            item.pricingCycle,
            selectedBillingCycle,
          );
        },
      }),
      columnHelper.display({
        id: "totalWalletCredits",
        header: "Total Wallet Credits",
        meta: { className: "plan-product-picker__col-computed" },
        cell: (info) => {
          const item = info.row.original;
          const creditsPerCycle = calculateCreditsPerBillingCycle(
            item.quantity,
            item.pricingCycle,
            selectedBillingCycle,
          );
          return (parseFloat(item.freeCredits) || 0) + creditsPerCycle;
        },
      }),
      columnHelper.accessor("basePrice", {
        id: "basePrice",
        header: `${entityLabel} price`,
        meta: { className: "plan-product-picker__col-computed" },
        cell: (info) => formatCurrency(info.getValue(), currencySign),
      }),
      columnHelper.accessor("pricingCycle", {
        id: "pricingCycle",
        header: "Pricing cycle",
        meta: { className: "plan-product-picker__col-computed" },
        cell: (info) => info.getValue() || "-",
      }),
      columnHelper.accessor("discountType", {
        id: "discountType",
        header: "Discount type",
        cell: (info) => (
          <Select
            compact
            options={DISCOUNT_TYPE_OPTIONS}
            value={info.getValue() ?? "Flat"}
            onChange={(value) =>
              handleDiscountChange(info.row.original.id, {
                discountType: value,
              })
            }
          />
        ),
      }),
      columnHelper.accessor("discount", {
        id: "discount",
        header: "Discount",
        cell: (info) => {
          const item = info.row.original;
          const discountType = item.discountType ?? "Flat";
          return (
            <TextInputWithPrefix
              compact
              prefix={discountType === "Percent" ? "%" : currencySign}
              placeholder={discountType === "Percent" ? "0" : "0.00"}
              value={info.getValue() ?? ""}
              onChange={(value) =>
                handleDiscountChange(item.id, { discount: value })
              }
            />
          );
        },
      }),
      columnHelper.accessor("totalPrice", {
        id: "totalPrice",
        header: "Total price",
        meta: { className: "plan-product-picker__col-total" },
        cell: (info) => formatCurrency(info.getValue(), currencySign),
      }),
      columnHelper.display({
        id: "actions",
        header: () => null,
        cell: (info) => (
          <Button
            variant="ghost-danger"
            size="md"
            iconOnly
            icon={<Trash2 size={16} />}
            onClick={() => handleRemove(info.row.original.id)}
            aria-label={`Remove ${info.row.original.label || "row"}`}
          />
        ),
      }),
    );

    return cols;
  }, [
    showBundle,
    bundleOptions,
    safeEntities,
    nameField,
    entityLabel,
    currencySign,
    selectedBillingCycle,
    handleBundleChange,
    handleEntityChange,
    handleAmountChange,
    handleQuantityChange,
    handleFreeCreditsChange,
    handleDiscountChange,
    handleRemove,
  ]);

  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  const allPrice = items.reduce(
    (sum, item) => sum + (parseFloat(item.totalPrice) || 0),
    0,
  );
  const columnCount = table.getVisibleFlatColumns().length;
  // Every price in this table (basePrice, creditsPerCycle, ...) is derived
  // against `selectedBillingCycle` - adding a row before it's chosen would
  // just price everything against `undefined` (silently falling back to
  // the entity's own cycle, unconverted) and re-price out from under the
  // user the moment they do pick one. Blocking Add here mirrors the
  // existing "Select bundle first" gate on the entity column.
  const addDisabled = !selectedBillingCycle;

  return (
    <div className="plan-product-picker">
      <h4 className="plan-product-picker__title">{title}</h4>

      {items.length > 0 && (
        <div className="plan-product-picker__scroll">
          <table className="plan-product-picker__catalog">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={header.column.columnDef.meta?.className}
                      aria-hidden={header.id === "actions" || undefined}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => {
                const item = row.original;
                const isTieredPricing = TIERED_MODELS.includes(
                  item.pricingModel,
                );
                return (
                  <Fragment key={row.id}>
                    <tr className="plan-product-picker__row">
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={cell.column.columnDef.meta?.className}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                    {isTieredPricing && (
                      <tr>
                        <td colSpan={columnCount}>
                          <TierTable
                            itemId={item.id}
                            tiers={item.pricingTiers}
                            quantity={item.quantity}
                            currencySign={currencySign}
                            onFieldChange={handleTierFieldChange}
                            onAdd={handleTierAdd}
                            onRemove={handleTierRemove}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}

              <tr className="plan-product-picker__summary-row">
                <td
                  colSpan={columnCount - 2}
                  className="plan-product-picker__summary-label"
                >
                  All {title.toLowerCase()} price
                </td>
                <td className="plan-product-picker__col-total">
                  {formatCurrency(allPrice, currencySign)}
                </td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className="plan-product-picker__add-footer">
        <Button
          variant="ghost"
          size="lg"
          onClick={handleAddRow}
          disabled={addDisabled}
        >
          + Add {entityLabel}
        </Button>
        {addDisabled && (
          <span className="plan-product-picker__add-hint">
            Select a billing cycle first
          </span>
        )}
      </div>
    </div>
  );
};

export default PlanProductPicker;
